'use client';

import { useMemo, useState } from 'react';
import Link from '@/components/NoPrefetchLink';
import { formatBirthday } from '@/lib/format';
import { withLocalePrefix, type Locale } from '@/lib/localePath';
import type { Profile } from '@/lib/types';

const text = (locale: Locale, ja: string, en: string, es: string) => locale === 'ja' ? ja : locale === 'es' ? es : en;
const PAGE_SIZE = 20;

export default function CareerOrganizationPlayerTable({ locale, players }: { locale: Locale; players: Profile[] }) {
  const [pagination, setPagination] = useState({ players, page: 1 });
  const sortedPlayers = useMemo(() => [...players].sort((a, b) => (b.birthday_date ?? '').localeCompare(a.birthday_date ?? '')), [players]);
  const totalPages = Math.ceil(players.length / PAGE_SIZE);
  const page = pagination.players === players ? Math.min(pagination.page, Math.max(1, totalPages)) : 1;
  const start = (page - 1) * PAGE_SIZE;
  const end = Math.min(page * PAGE_SIZE, players.length);
  const setPage = (next: number) => setPagination({ players, page: next });
  const pageItems: Array<number | 'ellipsis'> = totalPages <= 7
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
    : [1, ...(page > 3 ? ['ellipsis' as const] : []),
      ...Array.from({ length: Math.min(totalPages - 1, page + 1) - Math.max(2, page - 1) + 1 }, (_, i) => Math.max(2, page - 1) + i),
      ...(page < totalPages - 2 ? ['ellipsis' as const] : []), totalPages];
  const renderPagination = (position: 'top' | 'bottom') => totalPages > 1 && <nav className={`career-organization-pagination career-organization-pagination-${position}`} aria-label={text(locale, '選手一覧のページ切替', 'Player list pagination', 'Páginas de jugadores')}>
    <button type="button" onClick={() => setPage(page - 1)} disabled={page === 1}>{text(locale, '前へ', 'Prev', 'Anterior')}</button>
    <div className="career-organization-page-numbers">{pageItems.map((item, index) => item === 'ellipsis'
      ? <span key={`ellipsis-${index}`}>…</span>
      : <button key={item} type="button" onClick={() => setPage(item)} disabled={item === page} aria-current={item === page ? 'page' : undefined}>{item}</button>)}</div>
    <button type="button" onClick={() => setPage(page + 1)} disabled={page === totalPages}>{text(locale, '次へ', 'Next', 'Siguiente')}</button>
  </nav>;
  if (players.length === 0) return <p className="career-organization-empty">{text(locale, '該当する選手はいません。', 'No matching players.', 'No hay jugadores que coincidan.')}</p>;
  return <>
    <div className="career-organization-player-count">{text(locale,
      `${players.length}選手見つかりました（${start + 1}-${end}件表示）`,
      `${players.length} players found (showing ${start + 1}-${end})`,
      `${players.length} jugadores (mostrando ${start + 1}-${end})`)}</div>
    {renderPagination('top')}
    <div className="career-organization-table-wrap"><table className="career-organization-player-table">
    <thead><tr>
      <th>{text(locale, '選手名', 'Player', 'Jugador')}</th>
      <th className="career-organization-detail">{text(locale, '守備位置', 'Position', 'Posición')}</th>
      <th className="career-organization-detail">{text(locale, '国籍', 'Nationality', 'Nacionalidad')}</th>
      <th className="career-organization-detail">{text(locale, '生年月日', 'Birth date', 'Fecha de nacimiento')}</th>
    </tr></thead>
    <tbody>{sortedPlayers.slice(start, end).map((player) => <tr key={player.profile_id}>
      <td><Link href={withLocalePrefix(locale, `/players/${player.slug}`)}>{locale === 'ja' ? player.name : player.name_en ?? player.name}</Link></td>
      <td className="career-organization-detail">{locale === 'ja' ? player.position || '-' : player.position_en || player.position || '-'}</td>
      <td className="career-organization-detail">{locale === 'ja' ? player.nationality || '-' : locale === 'es' ? player.nationality_es || player.nationality_en || player.nationality || '-' : player.nationality_en || player.nationality || '-'}</td>
      <td className="career-organization-detail">{formatBirthday(player.birthday_date, false, locale)}</td>
    </tr>)}</tbody>
    </table></div>
    {renderPagination('bottom')}
  </>;
}
